import { produce } from 'solid-js/store';
import { useApiServices, useDataContext } from '@mobile-mapping/providers';
import { LoadState } from '@trimble/data-loading';
import { useConnectProject } from '@trimble/connect-project-providers';

interface UseRunStatusPollingResult {
  handleNodeExpand(nodeId: string): Promise<void>;
}

export function useRunStatusPolling(): UseRunStatusPollingResult {
  const { dataState, setDataState } = useDataContext();
  const { selectedTenantTrn } = useConnectProject();
  const { mobileMappingApi } = useApiServices();

  const handleNodeExpand = async (nodeId: string): Promise<void> => {
    if (!nodeId.startsWith('runs:')) return;

    const missionId = nodeId.replace('runs:', '');
    const missions = dataState.mission;
    if (missions.state !== LoadState.LoadComplete) return;

    const mission = missions.items[missionId];
    if (!mission) return;

    const tenantTrn = selectedTenantTrn();
    if (!tenantTrn) return;

    // Only fetch runs that are not complete (complete status won't change)
    const runsToFetch = mission.runs.filter((run) => run.status !== 'Complete');
    if (runsToFetch.length === 0) return;

    // Fetch runs in parallel (max 10 at a time)
    const chunkSize = 10;
    for (let i = 0; i < runsToFetch.length; i += chunkSize) {
      const chunk = runsToFetch.slice(i, i + chunkSize);

      const results = await Promise.all(
        chunk.map((run) =>
          mobileMappingApi.fetchRun(tenantTrn, mission.missionTrn, run.runId)
        )
      );

      setDataState(
        'mission',
        produce((missionState) => {
          if (missionState.state !== LoadState.LoadComplete) return;
          const missionItem = missionState.items[missionId];
          if (!missionItem) return;

          for (const result of results) {
            if (!result.pointCloudTrn || !result.status) continue;

            const runIndex = missionItem.runs.findIndex(
              (r) => r.id === result.pointCloudTrn
            );

            if (runIndex !== -1) {
              missionItem.runs[runIndex].status = result.status;
            }
          }
        })
      );
    }
  };

  return { handleNodeExpand };
}
